// ./src/components/layouts/AppShellLayout.tsx
//
// ROADMAP A2b #58 — THE APP SHELL, RENDERED ABOVE THE ROUTE INSTEAD OF INSIDE IT.
//
// ⚠⚠ WHAT #58 ACTUALLY FIXED. Until this existed, all 88 page surfaces rendered `DashboardLayout`
// themselves and `siteMap.ts` declared no `layout:` anywhere. React keeps a subtree mounted only
// while the element TYPE at that position is identical — and two pages are two different types — so
// every cross-page navigation threw the WHOLE tree away, shell included: the sidebar, its scroll
// position, the nav tree, the bell, the bottom tab bar, all rebuilt from scratch. Proven in the
// browser by tagging a DOM node belonging to the layout and watching it disappear on the next click.
//
// ⚠ `@sdflc/react-router-pages` HAS ALWAYS SUPPORTED THIS — it renders `<Layout><Page/></Layout>`
// per route, so naming ONE layout makes the shell the same component type on every route and React
// reconciles it instead of remounting. `siteMap.ts` even exported a `componentsMap` holding
// `DashboardLayout` for it; nothing ever passed the map to the router. So this is wiring that was
// half-built, not a new mechanism — which is also why `withPlanGate` had to memoise its wrapper
// per page (same bug, one level down).
//
// ⚠⚠ THE SUSPENSE BOUNDARY MOVED DOWN HERE, AND THAT IS HALF THE FIX. `App.tsx` keeps a boundary
// above `<Routes>`; with the shell above the route but the boundary above the shell, a first visit
// to any page (every page is a separate lazy chunk) would still replace the entire screen with the
// route fallback — the shell would stay MOUNTED but be hidden, which looks exactly like the bug
// #58 exists to fix. The boundary belongs INSIDE the shell so a downloading page shows its loader
// in the content area with the sidebar and header still on screen. The one in `App.tsx` stays as
// the backstop for the pages that have no shell (public, print, onboarding, the create wizard).
//
// ⚠ Pages do NOT hand this their header props any more — they publish them (`PageChrome` →
// `PageChromeContext`) and the shell spreads them back in. See that file for why the channel is a
// store rather than provider state.
import React, { Suspense } from "react";
import { PageProps } from "@sdflc/react-router-pages";

import { usePageChromeSnapshot } from "@/contexts/PageChromeContext";
import { RouteFallback } from "@/components/generic/RouteFallback";

import { DashboardLayout } from "./DashboardLayout";

interface AppShellLayoutProps extends PageProps {
  children?: React.ReactNode;
}

export const AppShellLayout: React.FC<AppShellLayoutProps> = ({ children, page }) => {
  const { ownerId, ...chrome } = usePageChromeSnapshot();

  return (
    // ⚠ `chromeOwnerId` is separated out rather than left in the spread: it is not a header prop,
    // it is the identity the shell keys its OWN header state on (the search box). Spreading it in
    // would compile — JSX does not excess-check a spread — and read as one more thing to render.
    <DashboardLayout page={page} chromeOwnerId={ownerId} {...chrome}>
      <Suspense fallback={<RouteFallback />}>{children}</Suspense>
    </DashboardLayout>
  );
};
